import { cn } from "../lib/cn";
import { CLAY_ART, type ClayArtName } from "../lib/clayArt";
import type { AccentName } from "../lib/accents";

// The glow behind the art, per accent (literal classes for Tailwind's JIT):
// the landing hero's "stage" blob, tilted toward the page's hue.
const GLOW: Record<AccentName, string> = {
  sky: "from-sky-200/80 via-violet-200/60 to-rose-200/70",
  rose: "from-rose-200/80 via-amber-100/70 to-violet-200/60",
  amber: "from-amber-200/80 via-rose-100/70 to-sky-200/60",
  emerald: "from-emerald-200/80 via-sky-100/70 to-amber-100/60",
  violet: "from-violet-200/80 via-sky-200/60 to-rose-200/60",
  indigo: "from-indigo-200/80 via-sky-200/60 to-violet-200/60",
};

export interface ClayArtProps {
  name: ClayArtName;
  /** Hue of the soft glow behind it. Default `sky`. */
  accent?: AccentName;
  /** Gentle float (frozen under reduced motion by globals.css). Default true. */
  float?: boolean;
  /** Above the fold (heroes): load eagerly instead of lazily. */
  priority?: boolean;
  /** Size it with width classes (e.g. `w-28 sm:w-40`); keep it under CLAY_ART[name].max. */
  className?: string;
}

/**
 * A clay illustration on a blurred pastel glow — the landing page's hero
 * treatment, sized for the inside pages. Always decorative (aria-hidden,
 * empty alt): the copy next to it carries the meaning.
 */
export function ClayArt({ name, accent = "sky", float = true, priority = false, className }: ClayArtProps) {
  const art = CLAY_ART[name];
  return (
    <div aria-hidden="true" className={cn("pointer-events-none relative select-none", className)}>
      <div
        className={cn(
          "absolute inset-x-[6%] bottom-[4%] top-[16%] rounded-[45%] bg-gradient-to-br blur-2xl",
          GLOW[accent],
        )}
      />
      <div className={cn("relative", float && "animate-float-slow")}>
        <img
          src={art.src}
          alt=""
          width={art.width}
          height={art.height}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          draggable={false}
          className="h-auto w-full"
        />
      </div>
    </div>
  );
}
